import { useEffect, useRef, useCallback } from "react";
import { Navigate, useLocation, useNavigate, useBlocker } from "react-router-dom";

import { Button } from "./components/shared/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import PageLayout from "./PageLayout";
import { useAuth } from "./contexts/AuthContext";
import { useSeminarStore } from "./stores/seminarStore";
import { useAutoLogout } from "./hooks/useAutoLogout";
import { useAppData } from "./hooks/useAppData";

/**
 * Auth gate + app shell. Data fetching lives in useAppData; this component only
 * handles auth gating, navigation side-effects, logout, and the unsaved-changes
 * guard.
 */
const ProtectedRoute = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { isAuth, userDetails, logOut } = useAuth();
  // editSeminar (the seminar edit-lock) is the single source of "unsaved work".
  const editSeminar = useSeminarStore((s) => s.editSeminar);
  const cancelEdit = useSeminarStore((s) => s.cancelEdit);

  // Log the user out after inactivity
  useAutoLogout();

  // Cross-cutting data layer: run for its side effects (loads the user, seminar
  // list, and WP token on auth). Pages read what they need from feature stores.
  useAppData();

  // First-load guard: a ref, not state — it's only read inside the effect and
  // never rendered, so it shouldn't trigger a re-render (or a set-state-in-effect).
  const firstLoadRef = useRef(true);

  // On first load, route into the main seminar view
  useEffect(() => {
    if (pathname !== "/seminare/semverwaltung" && userDetails && isAuth && firstLoadRef.current) {
      navigate("/seminare/semverwaltung", { replace: true });
    }
    firstLoadRef.current = false;
  }, [pathname, navigate, userDetails, isAuth]);

  // Unsaved-edit guard, done properly: React Router's data-router blocker
  // INTERCEPTS an in-app navigation before it happens (rather than the old
  // hack, which let the route change then forcibly bounced back to
  // semverwaltung). While a seminar is being edited, any route change is held
  // and the user gets a real choice — stay and keep editing, or discard and leave.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      editSeminar && currentLocation.pathname !== nextLocation.pathname
  );

  // The blocker only covers in-app navigation; a full page unload (tab close,
  // refresh, external link) needs the native beforeunload prompt.
  useEffect(() => {
    if (!editSeminar) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [editSeminar]);

  const handleLogout = useCallback(() => {
    logOut();
    navigate("/login");
  }, [logOut, navigate]);

  return (
    <>
      {isAuth === true && userDetails && (
        <>
          <PageLayout logOut={handleLogout} />
          <Dialog
            open={blocker.state === "blocked"}
            onClose={() => blocker.reset?.()}
            aria-labelledby="unsaved-edit-title"
            aria-describedby="unsaved-edit-description"
          >
            <DialogTitle id="unsaved-edit-title">Nicht gespeicherte Änderungen</DialogTitle>
            <DialogContent>
              <DialogContentText id="unsaved-edit-description">
                Sie bearbeiten dieses Seminar und haben Ihre Änderungen noch nicht gespeichert.
                Möchten Sie die Bearbeitung verlassen und die Änderungen verwerfen?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button size="sm" variant="grey" onClick={() => blocker.reset?.()}>
                Bleiben
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  // Discard the in-progress edit, then let the held navigation proceed.
                  cancelEdit();
                  blocker.proceed?.();
                }}
              >
                Verwerfen &amp; verlassen
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {isAuth === false && <Navigate to={{ pathname: "/login" }} />}
    </>
  );
};

export default ProtectedRoute;
